import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Trash2, Power, PowerOff, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { FormRule, RuleActionType, RuleConditionGroup, JobFieldMapping, PatientFieldMapping, PATIENT_MATCH_FIELDS } from "../../types/ruleTypes";
import { FormSection } from "../../types/formTypes";
import { ConditionBuilder } from "./ConditionBuilder";
import { ActionConfigurator } from "./ActionConfigurator";
import { PrefillFieldsConfigurator } from "./PrefillFieldsConfigurator";
import { PatientActionConfigurator } from "./PatientActionConfigurator";
import { JobDocumentTypeEnum } from "../../../job-page/job-document/job-document.types";
import { TooltipWrapper } from "../../../../components/ui/tooltip-wrapper";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";

interface InlineRuleItemProps {
	rule: FormRule;
	formSections: FormSection[];
	isNew?: boolean;
	onUpdate: (ruleId: string, updates: Partial<FormRule>) => void;
	onDelete: (ruleId: string) => void;
}

export const InlineRuleItem: React.FC<InlineRuleItemProps> = ({
	rule,
	formSections,
	isNew = false,
	onUpdate,
	onDelete,
}) => {
	const [isExpanded, setIsExpanded] = useState(isNew);

	const handleNameChange = (name: string) => {
		onUpdate(rule.id, { name });
	};

	const handleEnabledToggle = () => {
		onUpdate(rule.id, { enabled: !rule.enabled });
	};

	const handleConditionGroupChange = (conditionGroup: RuleConditionGroup) => {
		onUpdate(rule.id, { conditionGroup });
	};

	const handleConditionTypeChange = (type: "and" | "or") => {
		onUpdate(rule.id, {
			conditionGroup: { ...rule.conditionGroup, type },
		});
	};

	const handleActionTypeChange = (actionType: RuleActionType) => {
		if (actionType === "create_job_document") {
			onUpdate(rule.id, {
				action: {
					type: "create_job_document",
					documentType: JobDocumentTypeEnum.Quotation,
					templateId: null,
				},
			});
		} else if (actionType === "prefill_job_fields") {
			onUpdate(rule.id, {
				action: {
					type: "prefill_job_fields",
					fieldMappings: [],
				},
			});
		} else if (actionType === "create_patient") {
			onUpdate(rule.id, {
				action: {
					type: "create_patient",
					matchFields: ["patients.first_name", "patients.last_name"],
					fieldMappings: [],
				},
			});
		}
	};

	const handleDocumentTypeChange = (
		documentType: JobDocumentTypeEnum.Quotation | JobDocumentTypeEnum.DeliveryNote
	) => {
		if (rule.action.type === "create_job_document") {
			onUpdate(rule.id, {
				action: { ...rule.action, documentType },
			});
		}
	};

	const handleTemplateIdChange = (templateId: number | null) => {
		if (rule.action.type === "create_job_document") {
			onUpdate(rule.id, {
				action: { ...rule.action, templateId },
			});
		}
	};

	const handleFieldMappingsChange = (fieldMappings: JobFieldMapping[]) => {
		if (rule.action.type === "prefill_job_fields") {
			onUpdate(rule.id, {
				action: { ...rule.action, fieldMappings },
			});
		}
	};

	const handlePatientActionChange = (updates: {
		matchFields?: Array<typeof PATIENT_MATCH_FIELDS[number]>;
		fieldMappings?: PatientFieldMapping[];
	}) => {
		if (rule.action.type === "create_patient") {
			onUpdate(rule.id, {
				action: { ...rule.action, ...updates },
			});
		}
	};

	return (
		<div className="border border-gray-200 rounded-md bg-white">
			{/* Header - always visible */}
			<div className="flex items-center gap-3 p-3">
				<TooltipWrapper title={isExpanded ? "Einklappen" : "Aufklappen"} duration={150}>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setIsExpanded(!isExpanded)}
						className="h-6 w-6 p-0"
						aria-label={isExpanded ? "Einklappen" : "Aufklappen"}
					>
						{isExpanded ? (
							<ChevronDown className="h-4 w-4" />
						) : (
							<ChevronRight className="h-4 w-4" />
						)}
					</Button>
				</TooltipWrapper>

				<Input
					type="text"
					value={rule.name}
					onChange={(e) => handleNameChange(e.target.value)}
					placeholder="Regelname"
					className="flex-1"
					data-testid={`rule-name-${rule.id}`}
				/>

				<TooltipWrapper title={rule.enabled ? "Deaktivieren" : "Aktivieren"} duration={150}>
					<Button
						variant="ghost"
						size="sm"
						onClick={handleEnabledToggle}
						className={`h-8 w-8 p-0 ${rule.enabled ? "text-green-600" : "text-gray-400"
							}`}
						aria-label={rule.enabled ? "Deaktivieren" : "Aktivieren"}
						data-testid={`toggle-enabled-${rule.id}`}
					>
						{rule.enabled ? (
							<Power className="h-4 w-4" />
						) : (
							<PowerOff className="h-4 w-4" />
						)}
					</Button>
				</TooltipWrapper>

				<TooltipWrapper title="Regel löschen" duration={150}>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onDelete(rule.id)}
						className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
						aria-label="Regel löschen"
						data-testid={`delete-rule-${rule.id}`}
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</TooltipWrapper>
			</div>

			{/* Expanded content */}
			{isExpanded && (
				<div className="px-3 pb-3 space-y-4 border-t border-gray-200 pt-3">
					{/* Conditions Section */}
					<div className="space-y-2">
						<div className="flex items-center gap-2 flex-wrap">
							<span className="text-sm">Wenn</span>
							<Select
								value={rule.conditionGroup.type}
								onValueChange={(value: "and" | "or") =>
									handleConditionTypeChange(value)
								}
							>
								<SelectTrigger className="w-[100px] h-8">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="and">alle</SelectItem>
									<SelectItem value="or">eine</SelectItem>
								</SelectContent>
							</Select>
							<span className="text-sm">
								der folgenden Bedingungen erfüllt {rule.conditionGroup.conditions.length === 1 ? "ist" : "sind"}:
							</span>
						</div>

						<ConditionBuilder
							conditionGroup={rule.conditionGroup}
							formSections={formSections}
							onChange={handleConditionGroupChange}
							showTypeSelector={false}
						/>
						{rule.conditionGroup.conditions.length === 0 && (
							<div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
								<p className="text-sm text-amber-700">
									Bitte fügen Sie mindestens eine Bedingung hinzu, um die Aktion zu konfigurieren.
								</p>
							</div>
						)}
					</div>

					{/* Action Section */}
					{rule.conditionGroup.conditions.length > 0 && (
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<span className="text-sm">Dann</span>
								<Select
									value={rule.action.type}
									onValueChange={(value: RuleActionType) =>
										handleActionTypeChange(value)
									}
								>
									<SelectTrigger className="w-[280px] h-8">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="create_job_document">
											Auftragsdokument erstellen
										</SelectItem>
										<SelectItem value="prefill_job_fields">
											Auftragsinformationen vorausfüllen
										</SelectItem>
										<SelectItem value="create_patient">
											Patient anlegen
										</SelectItem>
									</SelectContent>
								</Select>
								<span className="text-sm">
									{rule.action.type === "create_job_document" ?
										"mit dieser Auswahl:" : rule.action.type === "prefill_job_fields" ? "mit folgenden Regeln:" : "mit folgenden Informationen:"}
								</span>
							</div>

							{rule.action.type === "create_job_document" ? (
								<ActionConfigurator
									ruleId={rule.id}
									documentType={rule.action.documentType}
									templateId={rule.action.templateId}
									onDocumentTypeChange={handleDocumentTypeChange}
									onTemplateIdChange={handleTemplateIdChange}
								/>
							) : rule.action.type === "prefill_job_fields" ? (
								<PrefillFieldsConfigurator
									ruleId={rule.id}
									fieldMappings={rule.action.fieldMappings}
									formSections={formSections}
									onChange={handleFieldMappingsChange}
								/>
							) : (
								<PatientActionConfigurator
									ruleId={rule.id}
									matchFields={rule.action.matchFields}
									fieldMappings={rule.action.fieldMappings}
									formSections={formSections}
									onPatientActionChange={handlePatientActionChange}
								/>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};
